import * as React from 'react';
import { createFileRoute, redirect } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Send, AlertTriangle, Mail } from 'lucide-react';

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select } from '@/components/ui/select';
import { Field } from '@/components/ui/field';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from '@/components/ui/sonner';
import { env } from '@/lib/env';
import { cn } from '@/lib/cn';

export const Route = createFileRoute('/_app/operator/conversations')({
  staticData: { crumb: 'Skrzynka' },
  beforeLoad: ({ context }) => {
    const ctx = context as { session: { role: string } };
    if (ctx.session.role !== 'operator') throw redirect({ to: '/' });
  },
  component: OperatorConversations,
});

type ConvState =
  | 'new'
  | 'intake'
  | 'drafting'
  | 'preview'
  | 'approved'
  | 'live'
  | 'escalated'
  | 'closed';

interface ConvRow {
  id: string;
  tenant_id: string | null;
  tenant_slug: string | null;
  tenant_legal_name: string | null;
  contact_email: string;
  state: ConvState;
  escalation_reason: string | null;
  last_preview_url: string | null;
  last_message_at: number | null;
  created_at: number;
}

interface MessageRow {
  id: string;
  conversation_id: string;
  direction: 'inbound' | 'outbound';
  from_addr: string;
  to_addr: string;
  subject: string;
  body_text: string | null;
  ts: number;
}

interface ConvDetail {
  conversation: ConvRow;
  messages: MessageRow[];
}

const STATE_LABEL: Record<ConvState, string> = {
  new: 'Nowa',
  intake: 'Wywiad',
  drafting: 'Brief',
  preview: 'Podgląd',
  approved: 'Zatwierdzona',
  live: 'Live',
  escalated: 'Eskalacja',
  closed: 'Zamknięta',
};

function OperatorConversations() {
  const [stateFilter, setStateFilter] = React.useState<ConvState | ''>('');
  const [escalatedOnly, setEscalatedOnly] = React.useState(false);
  const [minIdleDays, setMinIdleDays] = React.useState('');
  const [selectedId, setSelectedId] = React.useState<string | null>(null);

  const conversations = useQuery({
    queryKey: ['operator', 'conversations', stateFilter, escalatedOnly, minIdleDays],
    queryFn: async (): Promise<ConvRow[]> => {
      const url = new URL(`${env.apiBaseUrl}/api/operator/conversations`, window.location.origin);
      if (stateFilter) url.searchParams.set('state', stateFilter);
      if (escalatedOnly) url.searchParams.set('escalated', '1');
      if (minIdleDays) url.searchParams.set('min_days_idle', minIdleDays);
      const res = await fetch(url.toString(), { credentials: 'include' });
      if (!res.ok) throw new Error('failed_to_load');
      const j = (await res.json()) as { conversations: ConvRow[] };
      return j.conversations;
    },
    refetchInterval: 30_000,
  });

  return (
    <div className="space-y-4">
      <header>
        <h1 className="font-display text-2xl font-semibold tracking-tight text-text">
          Skrzynka rozmów
        </h1>
        <p className="mt-1 text-sm text-text-muted">
          Wszystkie konwersacje email-agenta we wszystkich klinikach. Odśwież co 30 s.
        </p>
      </header>

      <Card>
        <CardHeader>
          <CardTitle>Filtry</CardTitle>
          <CardDescription>Domyślnie pokazujemy wszystko, najnowsze na górze.</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid gap-3 md:grid-cols-3">
            <Field label="Stan" htmlFor="state">
              <Select
                id="state"
                value={stateFilter}
                onChange={(e) => setStateFilter(e.target.value as ConvState | '')}
              >
                <option value="">— wszystkie —</option>
                {(Object.keys(STATE_LABEL) as ConvState[]).map((s) => (
                  <option key={s} value={s}>
                    {STATE_LABEL[s]}
                  </option>
                ))}
              </Select>
            </Field>
            <Field label="Tylko eskalacje" htmlFor="esc">
              <label className="flex h-10 items-center gap-2 text-sm">
                <Switch id="esc" checked={escalatedOnly} onCheckedChange={setEscalatedOnly} />
                pokaż tylko stan „escalated"
              </label>
            </Field>
            <Field label="Min. dni bez odpowiedzi" htmlFor="idle">
              <Input
                id="idle"
                type="number"
                min={0}
                value={minIdleDays}
                onChange={(e) => setMinIdleDays(e.target.value)}
                placeholder="np. 3"
              />
            </Field>
          </div>
        </CardContent>
      </Card>

      <div className="grid gap-4 lg:grid-cols-[20rem_minmax(0,1fr)]">
        <ConversationList
          rows={conversations.data ?? []}
          loading={conversations.isPending}
          selectedId={selectedId}
          onSelect={setSelectedId}
        />
        {selectedId ? <ConversationDetail id={selectedId} /> : (
          <Card>
            <CardContent>
              <p className="rounded-md bg-surface p-4 text-sm text-text-muted ring-1 ring-black/5">
                Wybierz konwersację z listy obok, aby zobaczyć szczegóły.
              </p>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}

function ConversationList({
  rows,
  loading,
  selectedId,
  onSelect,
}: {
  rows: ConvRow[];
  loading: boolean;
  selectedId: string | null;
  onSelect: (id: string) => void;
}) {
  return (
    <Card className="max-h-[80vh] overflow-hidden">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm">{rows.length} konwersacji</CardTitle>
      </CardHeader>
      <CardContent className="overflow-y-auto p-0">
        {loading ? (
          <div className="space-y-2 p-4">
            <Skeleton className="h-16 w-full" />
            <Skeleton className="h-16 w-full" />
            <Skeleton className="h-16 w-full" />
          </div>
        ) : rows.length === 0 ? (
          <p className="p-4 text-sm text-text-muted">Brak konwersacji.</p>
        ) : (
          <ul role="list" className="divide-y divide-border">
            {rows.map((r) => (
              <li key={r.id}>
                <button
                  type="button"
                  onClick={() => onSelect(r.id)}
                  className={cn(
                    'w-full px-4 py-3 text-left transition',
                    selectedId === r.id ? 'bg-accent/5 ring-1 ring-inset ring-accent/20' : 'hover:bg-surface'
                  )}
                >
                  <div className="flex items-baseline justify-between gap-2">
                    <p className="truncate font-semibold text-text">
                      {r.tenant_legal_name ?? r.contact_email}
                    </p>
                    <StateBadge state={r.state} />
                  </div>
                  <p className="mt-0.5 truncate text-xs text-text-muted">
                    {r.contact_email}
                  </p>
                  <p className="mt-1 text-[10px] text-text-subtle">
                    {r.last_message_at
                      ? new Date(r.last_message_at * 1000).toLocaleString('pl-PL')
                      : 'brak wiadomości'}
                  </p>
                </button>
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}

function ConversationDetail({ id }: { id: string }) {
  const qc = useQueryClient();
  const detail = useQuery({
    queryKey: ['operator', 'conversation', id],
    queryFn: async (): Promise<ConvDetail> => {
      const res = await fetch(
        `${env.apiBaseUrl}/api/operator/conversations/${encodeURIComponent(id)}`,
        { credentials: 'include' }
      );
      if (!res.ok) throw new Error('failed_to_load');
      return (await res.json()) as ConvDetail;
    },
    refetchInterval: 30_000,
  });

  const takeOver = useMutation({
    mutationFn: async (reason?: string) => {
      const res = await fetch(
        `${env.apiBaseUrl}/api/operator/conversations/${encodeURIComponent(id)}/take-over`,
        {
          method: 'POST',
          credentials: 'include',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify({ reason }),
        }
      );
      if (!res.ok) throw new Error('take_over_failed');
      return res.json();
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['operator', 'conversations'] });
      qc.invalidateQueries({ queryKey: ['operator', 'conversation', id] });
    },
  });

  const reply = useMutation({
    mutationFn: async (input: { subject: string; body_text: string }) => {
      const res = await fetch(
        `${env.apiBaseUrl}/api/operator/conversations/${encodeURIComponent(id)}/reply`,
        {
          method: 'POST',
          credentials: 'include',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify(input),
        }
      );
      if (!res.ok) throw new Error('reply_failed');
      return res.json();
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['operator', 'conversation', id] });
      qc.invalidateQueries({ queryKey: ['operator', 'conversations'] });
      toast.success('Wiadomość wysłana');
    },
    onError: () => toast.error('Nie udało się wysłać wiadomości'),
  });

  const [subject, setSubject] = React.useState('');
  const [bodyText, setBodyText] = React.useState('');

  if (detail.isPending) {
    return (
      <Card>
        <CardContent>
          <p className="text-sm text-text-muted">Ładowanie…</p>
        </CardContent>
      </Card>
    );
  }
  if (!detail.data) return null;
  const { conversation: conv, messages } = detail.data;

  // Pre-fill reply subject with "Re: <last subject>".
  React.useEffect(() => {
    const lastInbound = [...messages].reverse().find((m) => m.direction === 'inbound');
    if (lastInbound && !subject) {
      setSubject(lastInbound.subject.startsWith('Re: ') ? lastInbound.subject : `Re: ${lastInbound.subject}`);
    }
  }, [messages]);

  const isTaken = conv.state === 'escalated';

  return (
    <div className="space-y-3">
      <Card>
        <CardHeader>
          <div className="flex flex-wrap items-start justify-between gap-2">
            <div className="min-w-0 flex-1">
              <CardTitle className="text-base">
                {conv.tenant_legal_name ?? conv.contact_email}
              </CardTitle>
              <CardDescription>
                {conv.contact_email}
                {conv.tenant_slug ? ` · ${conv.tenant_slug}` : ''}
                {' · stan: '}
                <span className="font-mono">{STATE_LABEL[conv.state]}</span>
              </CardDescription>
              {conv.escalation_reason ? (
                <p className="mt-1 inline-flex items-center gap-1 text-xs text-warning">
                  <AlertTriangle className="size-3" aria-hidden="true" />
                  {conv.escalation_reason}
                </p>
              ) : null}
            </div>
            {!isTaken ? (
              <Button
                type="button"
                size="sm"
                variant="secondary"
                onClick={() => takeOver.mutate('operator_take_over')}
                disabled={takeOver.isPending}
              >
                <AlertTriangle className="size-3.5" aria-hidden="true" />
                Przejmij konwersację
              </Button>
            ) : (
              <Badge variant="warning" className="text-xs">Operator prowadzi</Badge>
            )}
          </div>
        </CardHeader>
      </Card>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm">Wątek</CardTitle>
        </CardHeader>
        <CardContent>
          {messages.length === 0 ? (
            <p className="text-sm text-text-muted">Brak wiadomości.</p>
          ) : (
            <ul role="list" className="space-y-3">
              {messages.map((m) => (
                <li
                  key={m.id}
                  className={cn(
                    'rounded-md p-3 ring-1',
                    m.direction === 'inbound'
                      ? 'bg-surface ring-black/5'
                      : 'bg-accent/5 ring-accent/20'
                  )}
                >
                  <div className="flex flex-wrap items-baseline justify-between gap-2">
                    <p className="text-xs font-semibold text-text">
                      {m.direction === 'inbound' ? '← ' : '→ '}
                      {m.from_addr}
                    </p>
                    <p className="text-[10px] text-text-subtle">
                      {new Date(m.ts * 1000).toLocaleString('pl-PL')}
                    </p>
                  </div>
                  <p className="mt-1 text-xs text-text-muted">{m.subject}</p>
                  {m.body_text ? (
                    <pre className="mt-2 whitespace-pre-wrap text-sm text-text">{m.body_text}</pre>
                  ) : null}
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm">Ręczna odpowiedź</CardTitle>
          <CardDescription>
            Email zostanie wysłany z konta <span className="font-mono">agent@…</span>.
            Agent przestaje automatycznie odpowiadać po „Przejmij konwersację".
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <Field label="Temat" htmlFor="subj">
            <Input id="subj" value={subject} onChange={(e) => setSubject(e.target.value)} />
          </Field>
          <Field label="Treść" htmlFor="body">
            <Textarea
              id="body"
              rows={8}
              value={bodyText}
              onChange={(e) => setBodyText(e.target.value)}
              placeholder="Dzień dobry,&#10;&#10;…"
            />
          </Field>
          <div className="flex justify-end">
            <Button
              type="button"
              onClick={() => {
                if (!subject || !bodyText) return;
                reply.mutate(
                  { subject, body_text: bodyText },
                  {
                    onSuccess: () => {
                      setBodyText('');
                    },
                  }
                );
              }}
              disabled={reply.isPending || !subject || !bodyText}
            >
              <Send className="size-4" aria-hidden="true" />
              {reply.isPending ? 'Wysyłam…' : 'Wyślij odpowiedź'}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function StateBadge({ state }: { state: ConvState }) {
  const variant: Record<ConvState, 'soft' | 'warning' | 'success' | 'destructive' | 'outline'> = {
    new: 'soft',
    intake: 'soft',
    drafting: 'soft',
    preview: 'warning',
    approved: 'success',
    live: 'success',
    escalated: 'destructive',
    closed: 'outline',
  };
  return <Badge variant={variant[state]} className="text-[10px]">{STATE_LABEL[state]}</Badge>;
}

// silence lint
void Mail;
